import axios from 'axios';
import { toast } from 'react-toastify';
import { Capacitor } from '@capacitor/core'; // ⭐ Import Capacitor

// - 1. Dynamic API URL Selection -
const getBaseUrl = () => {
  // ⭐ NATIVE APP FIX: Force Production URL
  // When running as a Native App (APK/iOS), 'localhost' refers to the device
  // itself, not your PC, and there is no first-party domain for cookies to
  // attach to anyway — Capacitor apps keep hitting Cloud Run directly and
  // keep using the Authorization header + localStorage, unchanged.
  if (Capacitor.isNativePlatform()) {
    return 'https://service-os-api-london-142018845673.europe-west2.run.app';
  }

  const hostname = window.location.hostname;

  // ⭐ FIX: Match the theone. subdomain SPECIFICALLY, not any hostname that
  // merely contains "procleaneruk.co.uk". The old check also matched the
  // bare WordPress marketing site (procleaneruk.co.uk itself), which has no
  // Firebase Hosting rewrite at all — embedded forms there started resolving
  // to same-origin "/api/..." against WordPress, which has no such route,
  // causing 404s on the public form embed widget.
  if (hostname === 'theone.procleaneruk.co.uk' || hostname === 'theone.procleanerus.com') {
    return '';
  }

  // Bare marketing site (procleaneruk.co.uk / www.) — WordPress, not Firebase
  // Hosting, no rewrite exists. Embedded public forms here (form-embed.js)
  // must hit Cloud Run directly, cross-origin. This is fine since public
  // form endpoints don't require the auth cookie.
  if (hostname.includes('procleaneruk.co.uk') || hostname.includes('procleanerus.com')) {
    return 'https://service-os-api-london-142018845673.europe-west2.run.app';
  }

  // 🛠️ Localhost dev / anything else: use your .env override, since there's
  // no Firebase Hosting rewrite running on your local dev server.
  return process.env.REACT_APP_BACKEND_URL || 'https://service-os-api-usa-142018845673.us-central1.run.app';
};

const API_URL = getBaseUrl();
console.log(`🌐 API Routing: Connected to ${API_URL || '(same-origin)'}`);

// ⭐ NEW: CSRF token cache (web only — native is exempt, see csrfMiddleware.js
// on the backend). Held in memory only, never localStorage: it's not a
// secret on its own (it's useless without the matching httpOnly cookie), but
// there's no reason to persist it past a page reload since we fetch a fresh
// one lazily anyway.
let csrfToken = null;
let csrfTokenPromise = null; // de-dupes concurrent fetches on first load

const fetchCsrfToken = async () => {
    if (csrfTokenPromise) return csrfTokenPromise;
    csrfTokenPromise = api
        .get('/auth/csrf-token')
        .then((res) => {
            csrfToken = res.data.csrfToken;
            return csrfToken;
        })
        .catch((err) => {
            // Not logged in yet, or the endpoint failed — mutating requests
            // will go out without the header and the backend will reject
            // them with 403, which is the correct/safe failure mode.
            console.warn('[CSRF] Failed to fetch CSRF token:', err.message);
            return null;
        })
        .finally(() => {
            csrfTokenPromise = null;
        });
    return csrfTokenPromise;
};

// ⭐ NEW: Call this on logout so the next login fetches a fresh token bound
// to the new __session cookie, rather than reusing a stale one.
export const resetCsrfToken = () => {
    csrfToken = null;
};

// ⭐ NEW: SILENT RE-LOGIN (web only)
// The __session cookie only lasts 7 days (setAuthCookie maxAge in authController.js),
// and it is only renewed when /auth/firebase-login runs - which AuthContext does on page
// load / Firebase session restore. A tab or home-screen app left open for longer than
// that, or a cookie the browser dropped, means requests start arriving with NO login at
// all. protect() answers those with 403 "Not authorized, no token provided...", which the
// old code below never treated as a lost session - so the page carried on polling (live
// location, daily status) and got a 403 on every single request, forever.
//
// Firebase keeps its own sign-in on the device separately from our cookie, so in almost
// every case we can quietly get a new cookie instead of logging the person out:
//   1. ask Firebase for an ID token for the signed-in user
//   2. POST it to /auth/firebase-login, which sets a fresh __session cookie
//   3. retry the request that failed, once
// Only if that is impossible (Firebase is signed out too, or the server refuses) is the
// person logged out, ONCE, and sent to the login screen.
//
// Single-flight: if 20 requests fail at the same moment they all wait on the same
// re-login instead of each starting their own.
// Firebase is loaded with a dynamic import so the public form embed (which also uses
// this file) never pulls in the Firebase SDK unless a logged-in session needs it.
let reauthPromise = null;

const silentReauth = () => {
    if (reauthPromise) return reauthPromise;
    reauthPromise = (async () => {
        try {
            const { initializeFirebase } = await import('../firebase');
            const { auth } = await initializeFirebase();
            const firebaseUser = auth && auth.currentUser;
            if (!firebaseUser) {
                console.warn('[AUTH] Silent re-login not possible: no Firebase user on this device.');
                return false;
            }
            const idToken = await firebaseUser.getIdToken();
            const res = await api.post('/auth/firebase-login', { idToken }, { _skipAuthRefresh: true });
            if (!res.data || !res.data.user) {
                console.warn('[AUTH] Silent re-login refused by the server.');
                return false;
            }
            // New session cookie - the old CSRF token may not match it any more.
            resetCsrfToken();
            try {
                localStorage.setItem('user', JSON.stringify(res.data.user));
            } catch (e) { /* storage full / blocked - not critical */ }
            console.log('[AUTH] Session cookie renewed silently.');
            return true;
        } catch (err) {
            console.warn('[AUTH] Silent re-login failed:', err.response?.data?.message || err.message);
            return false;
        } finally {
            // Requests that failed at the same moment are already waiting on this
            // promise; clearing it now lets a later, separate session loss try again.
            reauthPromise = null;
        }
    })();
    return reauthPromise;
};

// ⭐ NEW: one clean logout when the session really is gone. The guard stops a burst of
// failing requests each firing their own toast/redirect. Clears the cached 'user' as
// well as 'token': AuthContext hydrates the logged-in state from the cached 'user', so
// leaving it behind is what made the app look logged in while every request failed.
const endSessionAndGoToLogin = async (message) => {
    if (window.__sessionLogoutStarted) return;
    window.__sessionLogoutStarted = true;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    resetCsrfToken();
    try {
        const { initializeFirebase } = await import('../firebase');
        const { auth } = await initializeFirebase();
        if (auth && auth.currentUser) await auth.signOut();
    } catch (e) { /* already signed out / offline - carry on */ }
    toast.error(message || 'Your session has expired. Please log in again.');
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    } else {
        window.__sessionLogoutStarted = false;
    }
};

// The exact wording protect() / protectCustomer() use when a request arrives with no
// login, or a login that failed. Normal permission 403s ("Forbidden: User role ...",
// suspended account, etc.) don't start with this, so they are left alone.
const isNoSessionResponse = (error) => {
    const status = error.response?.status;
    const message = String(error.response?.data?.message || '');
    return (status === 403 || status === 401) && message.startsWith('Not authorized');
};

// ⭐ NEW: Session-establishing routes — mirrors the CSRF_EXEMPT_PATH_PREFIXES
// list in the backend's index.js exactly. There's no point trying to fetch
// a CSRF token before these specific calls: no session exists yet at that
// moment, so GET /auth/csrf-token always 403s, and the actual request
// doesn't need the header anyway since the backend exempts it too. Skipping
// the attempt here just removes noise (a failed request + a console
// warning) for a case that was never actually broken.
// If you ever add/remove a route from that backend list, mirror the change
// here too — the two lists have no automatic way to stay in sync.
const CSRF_EXEMPT_PATHS = [
    '/auth/register-with-firebase',
    '/auth/firebase-login',
    '/auth/verify-2fa',
    '/auth/forgot-password',
    '/auth/passkey/login-start',
    '/auth/passkey/login-verify',
    // Sign-up via Stripe (no login yet) - mirrors the backend list
    '/stripe/create-sliding-scale-subscription',
    '/stripe/confirm-platform-subscription',
    '/stripe/create-setup-intent',
    '/stripe/create-pay-to-book-session',
    '/stripe/create-call-out-fee-session',
    '/stripe/create-deposit-payment-session',
];
const isCsrfExemptPath = (url) => CSRF_EXEMPT_PATHS.some((p) => (url || '').includes(p));

// - 2. Authenticated API Instance (Your main 'api' export) -
const api = axios.create({
  // Use the dynamically selected API_URL and append '/api'.
  // On web this resolves to just "/api" (same-origin, relative).
  baseURL: `${API_URL}/api`,
  // Cookies only make sense on web (same-origin now). Mobile keeps using its
  // existing Authorization header + localStorage method below, unaffected.
  withCredentials: !Capacitor.isNativePlatform(),
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ⭐ Native only: attach the Authorization header from localStorage, grabbed
// fresh at request time to avoid race conditions.
// Web: nothing to attach — the httpOnly `authToken` cookie rides along
// automatically via withCredentials: true above. Sending both used to mean
// the backend's authMiddleware could get a stale header token AND a fresh
// cookie in the same request and pick the wrong one; this removes that path
// entirely for web.
api.interceptors.request.use(
  async (config) => {
    if (Capacitor.isNativePlatform()) {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } else {
      // ⭐ NEW: Attach the CSRF header on web mutating requests. GET/HEAD/
      // OPTIONS are exempt on the backend by default, so only bother here
      // for the methods that actually need it — also avoids an unnecessary
      // token fetch on every read-only request.
      const method = (config.method || '').toLowerCase();
      const isMutating = ['post', 'put', 'delete', 'patch'].includes(method);
      if (isMutating && !isCsrfExemptPath(config.url)) {
        if (!csrfToken) {
          await fetchCsrfToken();
        }
        if (csrfToken) {
          config.headers['x-csrf-token'] = csrfToken;
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration (401 errors) and BLOB ERRORS
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    // ⭐ BLOB ERROR HANDLING FIX
    // If we requested a Blob (like a PDF) but the server returned an error (like 400/500),
    // Axios returns the error response as a Blob too. We must decode it to JSON to see the message.
    if (
      error.response &&
      error.response.data instanceof Blob &&
      error.response.data.type === 'application/json'
    ) {
      try {
        const text = await error.response.data.text();
        const errorObject = JSON.parse(text);
        
        // Replace the unreadable Blob with the actual JSON error object
        error.response.data = errorObject;
        
        // Update the error message so toasts can display it
        if (errorObject.message) {
            error.message = errorObject.message;
        }
      } catch (e) {
        console.error("Could not parse Blob error:", e);
      }
    }

    // ⭐ CSRF token rejected (e.g. the cached token went stale because
    // another tab fetched a newer one and overwrote the shared cookie).
    // Clear the cache, fetch a fresh token, and retry the ORIGINAL request
    // once with it — so the action that failed (logout, a save, etc.) just
    // succeeds instead of silently failing. _csrfRetry guards against
    // retrying more than once, so a genuinely broken case still fails
    // instead of looping forever.
    if (
      error.response &&
      error.response.status === 403 &&
      error.response.data?.code === 'EBADCSRFTOKEN'
    ) {
      const originalRequest = error.config;
      if (originalRequest && !originalRequest._csrfRetry && !originalRequest._skipAuthRefresh) {
        originalRequest._csrfRetry = true;
        console.warn('[CSRF] Token rejected by server — fetching a fresh one and retrying once.');
        resetCsrfToken();
        let freshToken = await fetchCsrfToken();
        // ⭐ NEW: No CSRF token because there's no login any more (cookie expired or
        // dropped) - renew the session silently, then try for a CSRF token again.
        if (!freshToken && !Capacitor.isNativePlatform() && !originalRequest._authRetry) {
          originalRequest._authRetry = true;
          const renewed = await silentReauth();
          if (renewed) {
            freshToken = await fetchCsrfToken();
          } else {
            await endSessionAndGoToLogin();
            return Promise.reject(error);
          }
        }
        if (freshToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers['x-csrf-token'] = freshToken;
          return api(originalRequest);
        }
        // Still no token - fall through and reject as before.
      } else {
        console.warn('[CSRF] Token rejected again after one retry — giving up, not looping.');
      }
    }

    // ⭐ NEW: Company's subscription was cancelled / failed while they were
    // logged in (authMiddleware.js kill-switch). Log them straight out with the
    // real reason instead of the generic "session expired" message. The guard
    // stops a burst of parallel requests each firing their own toast/redirect.
    if (
      error.response &&
      error.response.status === 401 &&
      error.response.data?.code === 'SUBSCRIPTION_INACTIVE'
    ) {
      if (!window.__subscriptionLogoutStarted) {
        window.__subscriptionLogoutStarted = true;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        resetCsrfToken();
        toast.error(error.response.data.message, { autoClose: 6000 });
        setTimeout(() => {
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          } else {
            window.location.reload();
          }
        }, 3000);
      }
      return Promise.reject(error);
    }

    // ⭐ NEW: Once the session has been ended and we're on the way to the login page,
    // don't start any more retries or re-logins for requests still in flight.
    if (!Capacitor.isNativePlatform() && window.__sessionLogoutStarted) {
      return Promise.reject(error);
    }

    // ⭐ NEW: WEB - lost session (cookie expired/dropped). Covers protect()'s 403
    // "Not authorized, no token provided..." AND real 401s (expired/invalid cookie).
    // Try a silent re-login and retry the request once; only log out if that fails.
    // Native is untouched: it sends its own token and keeps the shield below as before.
    if (
      !Capacitor.isNativePlatform() &&
      isNoSessionResponse(error) &&
      error.config &&
      !error.config._skipAuthRefresh
    ) {
      const requestUrl = error.config.url || '';
      const isThirdPartyTest = ['/settings/verify-twilio', '/settings/xero', '/settings/gocardless', '/settings/sage']
        .some((route) => requestUrl.includes(route));
      if (!isThirdPartyTest) {
        if (!error.config._authRetry) {
          error.config._authRetry = true;
          const renewed = await silentReauth();
          if (renewed) {
            // Mutating requests need a CSRF token for the new session.
            // (The request interceptor fetches a fresh one, since resetCsrfToken() ran.)
            if (error.config.headers && typeof error.config.headers.delete === 'function') {
              error.config.headers.delete('x-csrf-token');
            } else if (error.config.headers) {
              delete error.config.headers['x-csrf-token'];
            }
            return api(error.config);
          }
        }
        await endSessionAndGoToLogin();
        return Promise.reject(error);
      }
    }

    // ⭐ THE UPGRADED 401 SHIELD
    if (error.response && error.response.status === 401) {
      const requestUrl = error.config?.url || '';
      
      // NEW: Check if the request actually sent a token
      const hasAuthHeader = !!error.config?.headers?.['Authorization']; 
      // ⭐ FIX: On web, auth increasingly relies on the httpOnly cookie, which
      // JS can never see — so "no Authorization header" no longer reliably
      // means "race condition" the way it did when web always had a
      // localStorage token. Only treat a missing header as a harmless race
      // condition on native, where the header is genuinely the sole auth
      // mechanism and a missing one really would just be a startup timing gap.
      const isNative = Capacitor.isNativePlatform();

      // Array of routes that are allowed to fail with a 401 without killing the session
      const excludedRoutes = [
        '/settings/verify-twilio',
        '/settings/xero',        // <--- This now ignores /settings/xero/pay-items, etc.
        '/settings/gocardless',  // <--- Ignores all GoCardless errors
        '/settings/sage'         // <--- Ignores all Sage errors
      ];

      // Check if the failing URL contains any of our excluded paths
      const isExcluded = excludedRoutes.some(route => requestUrl.includes(route));

      // On native: no header genuinely can be a brief startup race condition —
      // keep the original "ignore it" behaviour.
      // On web: no header is now the NORMAL case (cookie carries auth instead),
      // so a real 401 here means the cookie itself is invalid/expired — treat
      // it as a genuine expiry, same as the header case below.
      const isHarmlessRaceCondition = isNative && !hasAuthHeader;

      if (!isExcluded && !isHarmlessRaceCondition) {
        localStorage.removeItem('token');
        toast.error('Your session has expired. Please log in again.');
        
        // Use window.location to force a redirect and full page reload.
        if (window.location.pathname !== '/login') {
           window.location.href = '/login';
        }
      } 
      else if (isHarmlessRaceCondition) {
        console.warn(`[API Shield] Ignored 401 for ${requestUrl} because the request had no token attached (Race Condition).`);
      } 
      else {
        // It is excluded. Just log it and let the component handle the red text.
        console.warn(`[API Shield] Safely ignored 401 error from third-party test: ${requestUrl}`);
      }
    }
    return Promise.reject(error);
  }
);

// - 3. Public API Instance (for non-authenticated routes) -
const publicApiClient = axios.create({
  // Use the dynamically selected API_URL and append '/api/public'
  baseURL: `${API_URL}/api/public`,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// - Export both instances -
export default api; // Default export for authenticated API
export { publicApiClient }; // Named export for public API